import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AuthContext from '../../context/AuthContext';
import Spinner from '../layout/Spinner';
import Alert from '../layout/Alert';

const EditPayment = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [formData, setFormData] = useState({
    projectId: '',
    amount: '',
    paymentMethod: '',
    paymentDate: '',
    checkNumber: '',
    referenceNumber: '',
    notes: ''
  });
  
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { projectId, amount, paymentMethod, paymentDate, checkNumber, referenceNumber, notes } = formData;
  
  // Format amount with commas
  const formatAmount = (value) => {
    if (!value) return value;
    
    // Remove non-numeric characters except decimal point
    let numericValue = value.toString().replace(/[^0-9.]/g, '');
    
    // Ensure only one decimal point
    const parts = numericValue.split('.');
    if (parts.length > 2) {
      numericValue = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Format with commas
    const formattedValue = numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    return formattedValue;
  };
  
  const onAmountChange = (e) => {
    const value = e.target.value;
    const numericValue = value.replace(/[^0-9.]/g, '');
    setFormData({ ...formData, amount: numericValue });
  };
  
  useEffect(() => {
    const fetchPaymentAndProjects = async () => {
      try {
        // Fetch payment details
        const paymentResponse = await fetch(`/api/payments/${id}`, {
          headers: {
            'x-auth-token': token
          }
        });
        
        // If payment not found, handle the error
        if (paymentResponse.status === 404) {
          setError({ msg: 'Payment not found' });
          setLoading(false);
          return;
        }
        
        if (!paymentResponse.ok) {
          const data = await paymentResponse.json();
          throw new Error(data.message || 'Failed to fetch payment details');
        }
        
        const paymentData = await paymentResponse.json();
        
        // Fetch projects list
        const projectsResponse = await fetch('/api/projects?noPagination=true', {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (!projectsResponse.ok) {
          const data = await projectsResponse.json();
          throw new Error(data.message || 'Failed to fetch projects');
        }
        
        const projectsData = await projectsResponse.json();
        
        // Format date from ISO to YYYY-MM-DD for input
        const formattedDate = new Date(paymentData.PaymentDate).toISOString().split('T')[0];
        
        // Set state with fetched data
        setFormData({
          projectId: paymentData.ProjectID.toString(),
          amount: paymentData.Amount.toString(),
          paymentMethod: paymentData.PaymentMethod,
          paymentDate: formattedDate,
          checkNumber: paymentData.CheckNumber || '',
          referenceNumber: paymentData.ReferenceNumber || '',
          notes: paymentData.Notes || ''
        });
        
        // Ensure projects is always an array
        if (Array.isArray(projectsData)) {
          setProjects(projectsData);
        } else if (projectsData && projectsData.items) {
          // Handle paginated response
          setProjects(projectsData.items);
        } else {
          // Fallback to empty array if neither condition is met
          setProjects([]);
          console.error('Projects data is not in the expected format:', projectsData);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError({ msg: err.message });
        setLoading(false);
      }
    };
    
    fetchPaymentAndProjects();
  }, [token, id]);
  
  const onChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const onSubmit = async e => {
    e.preventDefault();
    
    // Validate form
    if (!amount || !paymentMethod || !paymentDate) {
      setError({ msg: 'Please fill in all required fields' });
      return;
    }
    
    try {
      const response = await fetch(`/api/payments/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          amount: parseFloat(amount),
          paymentMethod,
          paymentDate,
          checkNumber: paymentMethod === 'Check' ? checkNumber : null,
          referenceNumber: ['Credit Card', 'PayPal', 'Bank Transfer'].includes(paymentMethod) ? referenceNumber : null,
          notes
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to update payment');
      }
      
      // Redirect to payments list
      navigate('/payments');
    } catch (err) {
      console.error('Error updating payment:', err);
      setError({ msg: err.message });
    }
  };
  
  if (loading) {
    return <Spinner />;
  }
  
  return (
    <div className="container">
      <h1 className="mb-4">Edit Payment</h1>
      
      {error && <Alert type="danger" message={error.msg} />}
      
      <form onSubmit={onSubmit}>
        <div className="mb-3">
          <label htmlFor="projectId" className="form-label">Project</label>
          <select
            className="form-select"
            id="projectId"
            name="projectId"
            value={projectId}
            disabled={true} // Cannot change project for existing payment
            readOnly
          >
            {Array.isArray(projects) && projects.length > 0 ? (
              projects.map(project => (
                <option key={project.ProjectId} value={project.ProjectId}>
                  {project.ProjectName}
                </option>
              ))
            ) : (
              <option value="">No projects available</option>
            )}
          </select>
          <small className="form-text text-muted">Project cannot be changed for an existing payment.</small>
        </div>
        
        <div className="mb-3">
          <label htmlFor="amount" className="form-label">Amount ($) *</label>
          <input
            type="text"
            className="form-control"
            id="amount"
            name="amount"
            value={formatAmount(amount)}
            onChange={onAmountChange}
            required
          />
        </div>
        
        <div className="mb-3">
          <label htmlFor="paymentMethod" className="form-label">Payment Method *</label>
          <select
            className="form-select"
            id="paymentMethod"
            name="paymentMethod"
            value={paymentMethod}
            onChange={onChange}
            required
          >
            <option value="">-- Select Payment Method --</option>
            <option value="Cash">Cash</option>
            <option value="Check">Check</option>
            <option value="Credit Card">Credit Card</option>
            <option value="PayPal">PayPal</option>
            <option value="Bank Transfer">Bank Transfer</option>
            <option value="Other">Other</option>
          </select>
        </div>
        
        <div className="mb-3">
          <label htmlFor="paymentDate" className="form-label">Payment Date *</label>
          <input
            type="date"
            className="form-control"
            id="paymentDate"
            name="paymentDate"
            value={paymentDate}
            onChange={onChange}
            required
          />
        </div>
        
        {paymentMethod === 'Check' && (
          <div className="mb-3">
            <label htmlFor="checkNumber" className="form-label">Check Number</label>
            <input
              type="text"
              className="form-control"
              id="checkNumber"
              name="checkNumber"
              value={checkNumber}
              onChange={onChange}
            />
          </div>
        )}
        
        {['Credit Card', 'PayPal', 'Bank Transfer'].includes(paymentMethod) && (
          <div className="mb-3">
            <label htmlFor="referenceNumber" className="form-label">Reference Number</label>
            <input
              type="text"
              className="form-control"
              id="referenceNumber"
              name="referenceNumber"
              value={referenceNumber}
              onChange={onChange}
            />
          </div>
        )}
        
        <div className="mb-3">
          <label htmlFor="notes" className="form-label">Notes</label>
          <textarea
            className="form-control"
            id="notes"
            name="notes"
            value={notes}
            onChange={onChange}
            rows="3"
          ></textarea>
        </div>
        
        <div className="d-flex justify-content-between">
          <button type="submit" className="btn btn-primary">
            Update Payment
          </button>
          <button 
            type="button" 
            className="btn btn-secondary"
            onClick={() => navigate('/payments')}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditPayment;